import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrokerDto, DispatchReviewDto, DriverDto, FreightBillingLineItemDto, JobDto, OrderByType, OrderDto, OrderReviewDto, OrderReviewLineItemDto, TicketEntryHaulerDto } from '../../../dtos';
import { AssignmentType } from '../../../dtos/generated/AssignmentType';
import { ReviewStatus } from '../../../dtos/generated/ReviewStatus';
import { useCreateBillingRatesFreightLineItemBillingRatesMutation,  useGetOrderQuery,  useLazyGetOrderReviewDispatchesQuery,  useUpdateOrderReviewDispatchesMutation } from '../../../store/generated/generatedApi';
import { emptyGuid, LineItemTypes } from '../../../util';
import {
    IFormFieldValidationConfig,
    IFormProps,
    createValidatorConfig,
    isNotBlank,
    runFormValidation,
    useFailedUpdateSnackbar,
    useSuccessfulUpdateSnackbar,
} from '../../CoreLib/library';
import { format } from 'date-fns';

export interface IOrderReviewFormValues {
    orderNumber?: number;
    orderDate: Date;
    clientId?: string;
    jobId: string;
    job?: JobDto;
    orderId?: string;
    order?: OrderDto;
    orderLineItems: OrderReviewLineItemDto[];
    isActive: boolean;
    poNumber: string;
    customerJobNumber: string;
    isTaxable?: boolean;
}

export const DEFAULT_ORDER_REVIEW: OrderReviewDto = {
    id: emptyGuid,
    clientId: emptyGuid,
    jobId: emptyGuid,
    orderId: emptyGuid,
    orderNumber: undefined,
    orderDate: new Date(),
    isActive: true,
    createdOn: new Date(),
    orderLineItems: [],
    status: ReviewStatus.InReview,
    statusName: 'In Review',
    readyForBilling: false,
    sentToQuickBooks: false,
    poNumber: '',
    customerJobNumber: '',
    isTaxable: undefined,
};

function isValidDate(value: any): { isValid: boolean; errorMessageBuilder: (fieldName: string) => string } {
    const isValid = !value || !isNaN(new Date(value).getTime());
    return {
        isValid,
        errorMessageBuilder: (fieldName: string) => `${fieldName} must be a valid date.`,
    };
}

function buildDispatchValidationConfig(dispatch: DispatchReviewDto): Map<keyof DispatchReviewDto, IFormFieldValidationConfig> {
    var validationConfig = new Map<keyof DispatchReviewDto, IFormFieldValidationConfig>();
    if (dispatch.assignmentType === AssignmentType.Broker) {
        validationConfig.set('brokerId', createValidatorConfig([isNotBlank], 'Broker'));
    } else {
        validationConfig.set('driverId', createValidatorConfig([isNotBlank], 'Driver'));
        validationConfig.set('equipmentId', createValidatorConfig([isNotBlank], 'Equipment'));
    }
    validationConfig.set('yardTime', createValidatorConfig([isValidDate], 'Yard Time'));
    validationConfig.set('startDate', createValidatorConfig([isValidDate], 'Start Date'));
    validationConfig.set('endDate', createValidatorConfig([isValidDate], 'End Date'));
    return validationConfig;
}

function buildFreightBillingValidationConfig(freightBilling: FreightBillingLineItemDto): Map<keyof FreightBillingLineItemDto, IFormFieldValidationConfig> {
    var validationConfig = new Map<keyof FreightBillingLineItemDto, IFormFieldValidationConfig>();
    validationConfig.set('freightBillNumber', createValidatorConfig([isNotBlank], 'Freight Bill #'));
    if (freightBilling.lineItemType === LineItemTypes.Material || freightBilling.lineItemType === LineItemTypes.Dump) {
        validationConfig.set('quantity', createValidatorConfig([isNotBlank], 'Quantity'));
    }
    return validationConfig;
}

export function useTicketEntryForm(props: IFormProps<OrderReviewDto>) {
    const { cancel } = props;
    const [currentDate, setCurrentDate] = useState<Date | undefined>();
    const [getBillingRates] = useCreateBillingRatesFreightLineItemBillingRatesMutation();
    const [isChanged, setIsChanged] = useState(false);
    const [formDispatchReviews, setFormDispatchReviews] = useState<DispatchReviewDto[]>([]);
    const [dispatchErrors, setDispatchErrors] = useState<Map<number, Map<keyof DispatchReviewDto, string>>>(new Map());
    const [freightErrors, setFreightErrors] = useState<Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>>>(new Map());
    const [saveDispatches, { isSuccess, isError, reset, error: updateError }] = useUpdateOrderReviewDispatchesMutation();
    const [getDispatches, { isFetching: isLoadingDispatches }] = useLazyGetOrderReviewDispatchesQuery();
    const [formHauler, setFormHauler] = useState<TicketEntryHaulerDto | undefined>();
    const { isLoading: isLoadingOrders, data: orderData } = useGetOrderQuery({
        searchText: '',
        sortKey: '',
        page: 0,
        pageSize: 100000,
        sortAsc: true,
        includeInactive: false,
        orderDate: currentDate?.toISOString(),
    });

    useSuccessfulUpdateSnackbar('Ticket', isSuccess, () => {});
    useFailedUpdateSnackbar('ticket', isError, reset, updateError);

    useEffect(() => {
        if (currentDate && formHauler) {
            getDispatches({ orderDate: format(currentDate, "yyyy-MM-dd"), haulerId: formHauler.haulerType === 'Driver' ? formHauler.driverId : formHauler.brokerId, equipmentId: formHauler.equipmentId })
                .then((response: any) => {
                    setFormDispatchReviews(response?.data ?? []);
                });
            
        }
    }, [currentDate, formHauler, getDispatches]);

    const getOrderById = useCallback(
        (orderId: string): OrderDto | undefined => {
            return orderData?.pageResults.find((order) => order.id === orderId);
        },
        [orderData]
    );

    const validateForm = useCallback(() => {
        var isValid = true;
        var dispatchValidationErrors: Map<number, Map<keyof DispatchReviewDto, string>> = new Map();
        var freightBillingValidationErrors: Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>> = new Map();

        formDispatchReviews.forEach((dispatch, dispatchIdx) => {
            const dispatchValidationConfig = buildDispatchValidationConfig(dispatch);
            const { isValid: isDispatchValid, errorMessages: dispatchErrorMessages } = runFormValidation<DispatchReviewDto>(dispatch, dispatchValidationConfig);
            !isDispatchValid && (isValid = false);

            var lineItemDispatchErrors: Map<keyof DispatchReviewDto, string> = new Map(dispatchErrorMessages);
            var dispatchFreightBillingValidationErrors: Map<number, Map<keyof FreightBillingLineItemDto, string>> = new Map();

            dispatch.freightBillingLineItems?.forEach((freightBillingLineItem, freightBillingIdx) => {
                const freightBillingValidationConfig = buildFreightBillingValidationConfig(freightBillingLineItem);
                const { isValid: isFreightBillingValid, errorMessages: freightBillingErrorMessages } = runFormValidation<FreightBillingLineItemDto>(
                    freightBillingLineItem,
                    freightBillingValidationConfig
                );
                !isFreightBillingValid && (isValid = false);

                const freightBillingErrorsMap: Map<keyof FreightBillingLineItemDto, string> = new Map(freightBillingErrorMessages);
                dispatchFreightBillingValidationErrors.set(freightBillingIdx, freightBillingErrorsMap);
            });

            dispatchValidationErrors.set(dispatchIdx, lineItemDispatchErrors);
            freightBillingValidationErrors.set(dispatchIdx, dispatchFreightBillingValidationErrors);
        });

        setDispatchErrors(dispatchValidationErrors);
        setFreightErrors(freightBillingValidationErrors);

        return isValid;
    }, [formDispatchReviews, setDispatchErrors, setFreightErrors]);

    const calculateBillingRates = useCallback(
        (billingRates: FreightBillingLineItemDto): Promise<FreightBillingLineItemDto> => {
            return getBillingRates(billingRates).then((response) => {
                return (response as any).data as FreightBillingLineItemDto;
            });
        },
        [getBillingRates]
    );

    const handleDispatchesChange = useCallback((dispatches: React.SetStateAction<DispatchReviewDto[]>) => {
        setFormDispatchReviews(dispatches);
        setIsChanged(true);
    }, []);

    const handleAddNewFreightBillingLineItem = useCallback(
        async (dispatchIndex: number) => {
            var updatedDispatches = _.cloneDeep(formDispatchReviews);
            var lineItem = updatedDispatches[dispatchIndex].orderLineItem;
            let freightBillingLineItem = {
                ...lineItem?.billingRates,
                id: emptyGuid,
                isActive: true,
                createdOn: new Date(),
            };

            if (lineItem?.orderBy === OrderByType.Quantity) {
                freightBillingLineItem.quantity = lineItem?.loadOrQuantityReq;
            }

            freightBillingLineItem = await calculateBillingRates(freightBillingLineItem);

            updatedDispatches[dispatchIndex].freightBillingLineItems!.push(freightBillingLineItem);
            setFormDispatchReviews(updatedDispatches);
            setIsChanged(true);
        },
        [calculateBillingRates, formDispatchReviews]
    );

    const handleRemoveFreightBillingLineItem = useCallback(
        (dispatchIndex: number, freightBillingLineItemIndex: number) => {
            var updatedDispatches = _.cloneDeep(formDispatchReviews);
            updatedDispatches[dispatchIndex].freightBillingLineItems!.splice(freightBillingLineItemIndex, 1);
            setFormDispatchReviews(updatedDispatches);
            setIsChanged(true);
        },
        [formDispatchReviews]
    );

    const createNewDispatchReview = useCallback(
            (formHauler: TicketEntryHaulerDto, orderReviewLineItem: OrderReviewLineItemDto) => {
                let driver: DriverDto | undefined = undefined;
                let broker: BrokerDto | undefined = undefined;
                if (formHauler?.haulerType === 'Driver') {
                    driver = formHauler.driver;
                } else if (formHauler?.haulerType === 'Broker') {
                    broker = formHauler.broker;
                }
                return {
                    assignmentType: formHauler?.haulerType === 'Driver' ? AssignmentType.Driver : AssignmentType.Broker,
                    id: emptyGuid,
                    isActive: true,
                    createdOn: new Date(),
                    orderLineItemId: orderReviewLineItem.id,
                    driverId: driver?.id,
                    driver: driver,
                    brokerId: broker?.id,
                    broker: broker,
                    startDate: orderReviewLineItem.onsiteTime,
                    endDate: undefined,
                    yardTime: orderReviewLineItem.yardTime,
                    equipmentId: formHauler?.equipmentId,
                    haulToAddressLine1: orderReviewLineItem.haulToAddressLine1 ?? '',
                    haulToAddressLine2: orderReviewLineItem.haulToAddressLine2 ?? '',
                    haulToCity: orderReviewLineItem.haulToCity ?? '',
                    haulToCountry: orderReviewLineItem.haulToCountry ?? '',
                    haulToState: orderReviewLineItem.haulToState ?? '',
                    haulToZipCode: orderReviewLineItem.haulToZipCode ?? '',
                    haulToRegion: orderReviewLineItem.haulToRegion,
                    haulToRegionId: orderReviewLineItem.haulToRegionId,
                    haulToSiteName: orderReviewLineItem.haulToSiteName,
                    haulFromAddressLine1: orderReviewLineItem.haulFromAddressLine1 ?? '',
                    haulFromAddressLine2: orderReviewLineItem.haulFromAddressLine2 ?? '',
                    haulFromCity: orderReviewLineItem.haulFromCity ?? '',
                    haulFromCountry: orderReviewLineItem.haulFromCountry ?? '',
                    haulFromState: orderReviewLineItem.haulFromState ?? '',
                    haulFromZipCode: orderReviewLineItem.haulFromZipCode ?? '',
                    haulFromRegion: orderReviewLineItem.haulFromRegion,
                    haulFromRegionId: orderReviewLineItem.haulFromRegionId,
                    haulFromSiteName: orderReviewLineItem.haulFromSiteName,
                    driverComment: '',
                    freightBillingLineItems: [],
                } as DispatchReviewDto;
            },
            []
        );
    
        const handleAddNewDispatchReviewForOrderReviewLineItem = useCallback(
            (formHauler: TicketEntryHaulerDto, orderReview: OrderReviewDto, orderReviewLineItem: OrderReviewLineItemDto) => {
                if (orderReviewLineItem) {
                    const newDispatchReview = createNewDispatchReview(formHauler, orderReviewLineItem);
                    const updatedOrderLineItem = _.cloneDeep(orderReviewLineItem);
                    updatedOrderLineItem.orderReview = orderReview;
                    newDispatchReview.orderLineItem = updatedOrderLineItem;
                    newDispatchReview.orderId = orderReview.orderId;
                    newDispatchReview.orderReview = orderReview;
                    setFormDispatchReviews([...formDispatchReviews, newDispatchReview]);
                    setIsChanged(true);
                }
            },
            [createNewDispatchReview, formDispatchReviews, setFormDispatchReviews]
        );

    const isFormDirty = useCallback(() => {
        return isChanged;
    }, [isChanged]);

    const isFormValid = useMemo(() => {
        return validateForm();
    }, [validateForm]);

    const handleSave = useCallback(() => {
        if (isFormValid) {
            saveDispatches(formDispatchReviews);

            setIsChanged(false);
        }
    }, [isFormValid, saveDispatches, formDispatchReviews]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    return {
        isFormDirty,
        isFormValid,
        handleSave,
        handleCancel,
        dispatchErrors,
        freightErrors,
        handleAddNewFreightBillingLineItem,
        handleRemoveFreightBillingLineItem,
        handleDispatchesChange,
        setFormDispatchReviews,
        formDispatchReviews,
        getOrderById,
        isLoadingDispatches,
        handleAddNewDispatchReviewForOrderReviewLineItem,
        setCurrentDate,
        currentDate,
        formHauler,
        setFormHauler,
        isLoadingOrders
    };
}

export function isErrorInMap(errors: Map<string, string> | undefined): boolean {
    if (!errors) {
        return false;
    }
    return Array.from(errors.values()).some((x) => x !== '');
}
